/* A.K. TINSON motion layer (2026-10-25). Add-on for the live TinSon Trade theme; load after trade.js.
   It only listens: trade.js still owns the quote list (localStorage ak-tinson-trade-quote-v1). */
(() => {
  'use strict';
  if (window.akTinsonMotion) return; // never build twice if the file is included twice
  window.akTinsonMotion = true;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const EASE_IO = 'cubic-bezier(.645,.045,.355,1)';
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const store = {
    get(k) { try { return localStorage.getItem(k); } catch { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch { /* storage blocked */ } },
  };

  /* ---------- Shared: send a label flying from one element to another ---------- */
  function fly(fromEl, toEl, label, duration = 620) {
    if (reduce || !fromEl || !toEl || !fromEl.animate) return Promise.resolve();
    const a = fromEl.getBoundingClientRect();
    const b = toEl.getBoundingClientRect();
    if (!b.width) return Promise.resolve();
    const tag = document.createElement('div');
    tag.className = 'm-fly';
    tag.textContent = label;
    tag.setAttribute('aria-hidden', 'true');
    document.body.append(tag);
    const w = tag.offsetWidth;
    const h = tag.offsetHeight;
    const x0 = a.left + a.width / 2 - w / 2;
    const y0 = a.top + a.height / 2 - h / 2;
    const x1 = b.left + b.width / 2 - w / 2;
    const y1 = b.top + b.height / 2 - h / 2;
    const lift = Math.min(140, Math.abs(y0 - y1) * 0.3 + 48);
    return tag.animate([
      { transform: `translate(${x0}px, ${y0}px) scale(1)`, opacity: 1 },
      { transform: `translate(${(x0 + x1) / 2}px, ${Math.min(y0, y1) - lift}px) scale(.92)`, opacity: 1, offset: 0.55 },
      { transform: `translate(${x1}px, ${y1}px) scale(.4)`, opacity: 0.15 },
    ], { duration, easing: EASE_IO }).finished.catch(() => {}).then(() => tag.remove());
  }

  function bump(el, ringEl) {
    if (!el) return;
    el.classList.remove('m-bump');
    void el.offsetWidth;
    el.classList.add('m-bump');
    const ring = ringEl || el;
    ring.classList.remove('m-ring');
    void ring.offsetWidth;
    ring.classList.add('m-ring');
    setTimeout(() => ring.classList.remove('m-ring'), 700);
  }

  /* ---------- 1. Add to quote ---------- */
  const visibleCount = () => [...document.querySelectorAll('[data-quote-count]')].find((n) => n.offsetParent !== null);
  let pending = null;

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-quote-add]')) return;
    const select = form.querySelector('select[name="id"]');
    const option = select?.selectedOptions[0] || form.querySelector('input[name="id"]:checked');
    const qty = Number(form.querySelector('[name="quantity"]')?.value || 1) || 1;
    const pack = option?.dataset.title || option?.textContent?.trim() || '';
    pending = { button: form.querySelector('button[type="submit"]'), label: `+${qty} × ${pack}`.trim(), at: Date.now() };
  }, true);

  function confirmButton(button) {
    if (!button || button.classList.contains('m-added')) return;
    const original = button.innerHTML;
    button.classList.add('m-added');
    button.innerHTML = 'Added <span class="m-check" aria-hidden="true">✓</span>';
    setTimeout(() => { button.classList.remove('m-added'); button.innerHTML = original; }, 1500);
  }

  const counter = document.querySelector('[data-quote-count]');
  if (counter) {
    let last = Number(counter.textContent) || 0;
    new MutationObserver(() => {
      const now = Number(counter.textContent) || 0;
      const grew = now > last;
      last = now;
      if (!grew || !pending || Date.now() - pending.at > 1500) return;
      const { button, label } = pending;
      pending = null;
      confirmButton(button);
      const target = visibleCount();
      const targetBox = target?.closest('a, button') || target;
      fly(button, targetBox, label).then(() => bump(target, targetBox));
    }).observe(counter, { childList: true, characterData: true, subtree: true });
  }

  /* ---------- 2. First-visit hint on the first Add to quote button ---------- */
  const firstAdd = document.querySelector('.product-card form[data-quote-add] button[type="submit"], form[data-quote-add] button[type="submit"]');
  if (firstAdd && !reduce && !store.get('ak-motion-hint') && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      setTimeout(() => {
        firstAdd.classList.add('m-hint');
        store.set('ak-motion-hint', '1');
        setTimeout(() => firstAdd.classList.remove('m-hint'), 3000);
      }, 600);
    }, { threshold: 1 });
    io.observe(firstAdd);
  }

  /* ---------- 3. "Three steps. One quote." demo: explore, pick, size, add, fill the form, send (looping) ---------- */
  // Real catalogue cards on the page (homepage), else the four featured products fetched from the store.
  // Each card carries its packs and each pack's own photo, so choosing the drum shows the drum.
  const FEATURED = ['sles-70-philippines', 'cocamidopropyl-betaine-capb', 'castor-oil', 'polysorbate-80-tween-80'];
  const sized = (src, w) => {
    if (!src) return '';
    return /[?&]width=\d+/.test(src) ? src.replace(/([?&]width=)\d+/, `$1${w}`) : `${src}${src.includes('?') ? '&' : '?'}width=${w}`;
  };
  const cardsFromPage = () => [...document.querySelectorAll('.product-card')].slice(0, 4).map((c) => {
    const img = c.querySelector('img');
    const options = [...c.querySelectorAll('select[name="id"] option')];
    return {
      title: c.querySelector('h3')?.textContent.trim(),
      type: c.querySelector('.product-type')?.textContent.trim() || '',
      img: img ? (img.currentSrc || img.src) : '',
      packs: options.map((o) => o.dataset.title || o.textContent.trim()),
      packImgs: options.map((o) => sized(o.dataset.previewImage || o.dataset.image || '', 500)),
    };
  }).filter((c) => c.title && c.packs.length);
  const cardsFromStore = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const found = await Promise.all(FEATURED.map((h) => fetch(`${root}products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)));
    return found.filter(Boolean).map((p) => ({
      title: p.title,
      type: p.type || '',
      img: sized(p.featured_image, 400),
      packs: (p.variants || []).map((v) => v.title),
      packImgs: (p.variants || []).map((v) => sized(v.featured_image?.src || '', 500)),
    }));
  };

  const steps = document.querySelector('.vt-steps');
  if (steps && steps.querySelector('ol')) {
    // Show how quoting works right after the hero, before visitors browse (it is below the first screen, so nothing jumps).
    const trust = document.querySelector('.vt-trust');
    if (trust && trust.nextElementSibling !== steps) trust.after(steps);
    buildDemo(steps, cardsFromPage());
  }

  // Wholesale enquiries / request-a-quote page: a "how it works" demo under the page heading.
  const quoteHeading = document.querySelector('.quote-section .section-heading');
  if (quoteHeading) {
    const host = document.createElement('div');
    host.className = 'm-demo-host';
    host.innerHTML = '<p class="m-demo-host__h">How a quote request works</p>';
    quoteHeading.after(host);
    cardsFromStore().then((cards) => buildDemo(host, cards), () => buildDemo(host, []));
  }

  function buildDemo(section, pageCards) {
    const items = [...section.querySelectorAll('ol > li')];
    const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    let cards = (pageCards || []).filter((c) => c.title && c.packs.length);
    if (cards.length < 2) {
      cards = [
        { title: 'Sodium Laureth Sulfate (SLES) 70%', type: 'Surfactants & foam control', img: '', packs: ['1 kg', 'Box (25 kg)', 'Drum (170 kg)'], packImgs: [] },
        { title: 'Cocamidopropyl Betaine (CAPB)', type: 'Surfactants & foam control', img: '', packs: ['1 kg', 'Carboy (20 kg)'], packImgs: [] },
      ];
    }
    const pick = cards[0];
    const packs = pick.packs.slice(0, 4);
    const packImgs = packs.map((_, i) => (pick.packImgs || [])[i] || pick.img);
    const drumIndex = packs.findIndex((p) => /drum/i.test(p));
    const target = drumIndex > -1 ? drumIndex : packs.length - 1;
    const imgTag = (src) => (src ? `<img src="${esc(src)}" alt="" loading="lazy">` : '');

    section.classList.add('m-has-demo');
    const demo = document.createElement('div');
    demo.className = 'm-demo';
    demo.setAttribute('role', 'region');
    demo.setAttribute('aria-label', 'How a quote request works: browse the catalogue, pick a material, choose a pack size and quantity, press Add to quote, fill in your details on the quote page, and send the quote request.');
    demo.innerHTML = `
      <div class="m-demo__bar" aria-hidden="true">
        <span class="m-demo__brand">A.K. TINSON</span>
        <span class="m-demo__quote" data-q>Your quote <span class="m-demo__count">0</span></span>
      </div>
      <div class="m-demo__screen">
        <div class="m-demo__track">
          <div class="m-scene" aria-hidden="true">
            <p class="m-scene__h">Material catalogue</p>
            <div class="m-grid">${cards.map((c, i) => `<div class="m-card" data-card="${i}"><div class="m-card__img">${imgTag(c.img)}</div><span class="m-card__type">${esc(c.type)}</span><span class="m-card__name">${esc(c.title)}</span></div>`).join('')}</div>
          </div>
          <div class="m-scene">
            <div class="m-prod">
              <div class="m-prod__img" aria-hidden="true">${imgTag(pick.img)}</div>
              <div>
                <p class="m-prod__type" aria-hidden="true">${esc(pick.type)}</p>
                <p class="m-prod__name" aria-hidden="true">${esc(pick.title)}</p>
                <span class="m-prod__tag" aria-hidden="true">Quoted for your requirements</span>
                <span class="m-label" id="m-pack-label-${section.className.length}">Pack size, tap to see each pack</span>
                <div class="m-packs" role="group">${packs.map((p, i) => `<button type="button" class="m-pack${i === 0 ? ' is-on' : ''}" data-pack="${i}" tabindex="-1">${esc(p)}</button>`).join('')}</div>
                <div class="m-row" aria-hidden="true"><span class="m-qty"><span>−</span><b>1</b><span data-plus>+</span></span><span class="m-add">Add to quote +</span></div>
              </div>
            </div>
          </div>
          <div class="m-scene m-scene--form" aria-hidden="true">
            <div class="m-form">
              <div>
                <p class="m-scene__h">Your quote list</p>
                <div class="m-qline">${imgTag(packImgs[target]) || '<span></span>'}<span><span class="m-qline__name">${esc(pick.title)}</span><br><span class="m-qline__meta">${esc(packs[target])}</span></span><span class="m-qline__qty">× 2</span></div>
              </div>
              <div>
                <p class="m-scene__h">Tell us about your business</p>
                <div class="m-fields">
                  <span class="m-field"><span>Your name *</span><i data-type="Maria Santos"></i></span>
                  <span class="m-field"><span>Company / business *</span><i data-type="Santos Home Care"></i></span>
                  <span class="m-field"><span>Email address *</span><i data-type="maria@example.com"></i></span>
                  <span class="m-field"><span>Contact number *</span><i data-type="0917 000 0000"></i></span>
                </div>
                <span class="m-send">Send quote request ↗</span>
              </div>
            </div>
            <div class="m-done"><span class="m-done__tick">✓</span><span class="m-done__h">Your request is on its way.</span><span class="m-done__p">Our team replies with pricing, stock and delivery.</span></div>
          </div>
        </div>
      </div>
      <div class="m-demo__foot"><p class="m-demo__caption" aria-hidden="true"></p><button class="m-demo__toggle" type="button" aria-pressed="false">Pause</button></div>
      <svg class="m-demo__cursor" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 2l15 9-6.5 1.6L16 20l-3 1.5-3.4-7.3L4 18z" fill="#142832" stroke="#fff" stroke-width="1.4" stroke-linejoin="round"/></svg>`;
    section.append(demo);

    const $ = (s) => demo.querySelector(s);
    const $$ = (s) => [...demo.querySelectorAll(s)];
    const track = $('.m-demo__track');
    const cursor = $('.m-demo__cursor');
    const caption = $('.m-demo__caption');
    const prodImg = $('.m-prod__img');
    const qty = $('.m-qty b');
    const add = $('.m-add');
    const quoteBtn = $('[data-q]');
    const count = $('.m-demo__count');
    const send = $('.m-send');
    const done = $('.m-done');
    const toggle = $('.m-demo__toggle');

    // Playback state: the loop runs only while the demo is on screen and not paused.
    const ABORT = Symbol('abort');
    let runId = 0;
    let visible = false;
    let paused = false;
    const gate = () => new Promise((resolve) => {
      const check = () => (visible && !paused ? resolve() : setTimeout(check, 200));
      check();
    });
    const tick = async (id, ms) => {
      await gate();
      if (id !== runId) throw ABORT;
      await wait(ms);
      if (id !== runId) throw ABORT;
    };

    const scene = (i) => { track.style.transform = `translateX(${-i * 33.3333}%)`; };
    const moveTo = (el, fx = 0.6, fy = 0.55) => {
      const d = demo.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      cursor.style.transform = `translate(${r.left - d.left + r.width * fx}px, ${r.top - d.top + r.height * fy}px)`;
    };
    const say = (text) => {
      caption.style.opacity = '0';
      setTimeout(() => { caption.textContent = text; caption.style.opacity = '1'; }, 160);
    };
    const stepOn = (i) => items.forEach((li, j) => li.classList.toggle('m-step-on', j === i));
    // Show a pack: highlight its chip and swap the product photo to that pack's own photo.
    const showPack = (i) => {
      $$('.m-pack').forEach((p, j) => p.classList.toggle('is-on', j === i));
      const src = packImgs[i];
      const img = prodImg.querySelector('img');
      if (!src || !img || img.getAttribute('src') === src) return;
      if (reduce || !img.animate) { img.src = src; return; }
      img.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 140, easing: 'ease', fill: 'forwards' }).finished.then(() => {
        img.src = src;
        const show = () => img.animate([{ opacity: 0, transform: 'scale(.97)' }, { opacity: 1, transform: 'none' }], { duration: 260, easing: 'cubic-bezier(.165,.84,.44,1)', fill: 'forwards' });
        if (img.complete) show(); else img.addEventListener('load', show, { once: true });
      });
    };
    const press = async (id, el, ms = 160) => { el.classList.add('is-press'); try { await tick(id, ms); } finally { el.classList.remove('is-press'); } };
    const type = async (id, el) => {
      const text = el.dataset.type;
      for (let i = 1; i <= text.length; i++) { el.textContent = text.slice(0, i); await tick(id, 32); }
    };

    const reset = () => {
      track.style.transition = 'none';
      scene(0);
      void track.offsetWidth;
      track.style.transition = '';
      $$('.m-card').forEach((c) => c.classList.remove('is-hover', 'is-press'));
      showPack(0);
      qty.textContent = '1';
      add.className = 'm-add';
      add.textContent = 'Add to quote +';
      count.textContent = '0';
      $$('.m-field').forEach((f) => f.classList.remove('is-focus'));
      $$('[data-type]').forEach((f) => { f.textContent = ''; });
      done.classList.remove('is-on');
      cursor.style.opacity = '1';
      cursor.style.transition = 'none';
      cursor.style.transform = 'translate(24px, 70px)';
      void cursor.offsetWidth;
      cursor.style.transition = '';
    };

    async function cycle(id) {
      // 01 Explore: look through the catalogue, pick a material
      stepOn(0); say('Explore the catalogue.');
      await tick(id, 400);
      const visibleCards = $$('.m-card').filter((c) => c.offsetParent !== null);
      for (const c of visibleCards.slice(1).reverse().concat(visibleCards[0])) {
        visibleCards.forEach((v) => v.classList.toggle('is-hover', v === c));
        moveTo(c, 0.5, 0.4);
        await tick(id, 650);
      }
      say('Pick a material.');
      await press(id, visibleCards[0], 220);
      scene(1);
      await tick(id, 650);

      // 02 Build: choose a pack size (the photo changes to that pack) and quantity, add to quote
      stepOn(1); say(`Choose a pack size. The photo shows the ${packs[target]}.`);
      moveTo($(`[data-pack="${target}"]`)); await tick(id, 650);
      showPack(target); await tick(id, 900);
      say('Set how many packs.');
      moveTo($('[data-plus]')); await tick(id, 650);
      qty.textContent = '2';
      qty.animate?.([{ transform: 'scale(1.35)' }, { transform: 'scale(1)' }], { duration: 220, easing: 'ease-out' });
      await tick(id, 550);
      say('Add it to your quote.');
      moveTo(add); await tick(id, 650);
      await press(id, add, 140);
      add.className = 'm-add is-done'; add.textContent = 'Added ✓';
      await fly(add, quoteBtn, `+2 × ${packs[target]}`, 700);
      count.textContent = '1'; bump(count, quoteBtn);
      await tick(id, 700);

      // 03 Enquire: open Your quote, fill in the form, send
      stepOn(2); say('Open Your quote.');
      moveTo(quoteBtn, 0.5, 0.5); await tick(id, 650);
      await press(id, quoteBtn, 160);
      scene(2);
      await tick(id, 700);
      say('Fill in your details.');
      for (const field of $$('.m-field')) {
        moveTo(field.querySelector('i'), 0.2, 0.5);
        await tick(id, 450);
        $$('.m-field').forEach((f) => f.classList.toggle('is-focus', f === field));
        await type(id, field.querySelector('i'));
        await tick(id, 150);
      }
      $$('.m-field').forEach((f) => f.classList.remove('is-focus'));
      say('Send your quote request.');
      moveTo(send, 0.5, 0.5); await tick(id, 650);
      await press(id, send, 180);
      done.classList.add('is-on');
      say('Our team replies with pricing, stock and delivery.');
      await tick(id, 2800);
    }

    async function run() {
      const id = ++runId;
      section.classList.add('m-playing');
      try {
        for (;;) { reset(); await cycle(id); }
      } catch (err) {
        if (err !== ABORT) throw err;
      }
    }

    const setToggle = (playing) => {
      toggle.textContent = playing ? 'Pause' : 'Play';
      toggle.setAttribute('aria-pressed', String(!playing));
    };
    toggle.addEventListener('click', () => {
      if (toggle.textContent === 'Pause') { paused = true; setToggle(false); return; }
      paused = false;
      setToggle(true);
      if (demo.dataset.manual === '1') { demo.dataset.manual = ''; run(); }
    });

    // Visitors can tap a pack in the demo to see that pack's photo; the loop pauses on the product.
    $$('.m-pack').forEach((btn) => btn.addEventListener('click', () => {
      runId++;
      demo.dataset.manual = '1';
      paused = false;
      setToggle(false);
      track.style.transition = '';
      scene(1);
      cursor.style.opacity = '0';
      items.forEach((li) => li.classList.toggle('m-step-on', li === items[1]));
      const i = Number(btn.dataset.pack);
      showPack(i);
      caption.textContent = `Showing the ${packs[i]} pack. Press Play to continue the walkthrough.`;
    }));

    if (reduce || !('IntersectionObserver' in window)) {
      // No autoplay: show the product with the drum chosen; packs stay clickable.
      scene(1);
      showPack(target);
      cursor.style.display = 'none';
      toggle.hidden = true;
      caption.textContent = 'Choose a pack size to see it, then add it to your quote and send one request.';
      return;
    }
    reset();
    new IntersectionObserver((entries) => {
      visible = entries.some((e) => e.isIntersecting);
      if (visible && !runId) run();
    }, { threshold: 0.35 }).observe(demo);
  }

  /* ---------- 4. Quote list ---------- */
  const list = document.querySelector('[data-quote-items]');
  if (list && !reduce) {
    // trade.js usually renders before this script runs, so animate rows already present, else the first render.
    let first = true;
    const enter = () => {
      const rows = list.querySelectorAll('.quote-item');
      if (!rows.length || !first) return;
      first = false;
      rows.forEach((row, i) => { row.style.setProperty('--m-i', Math.min(i, 8)); row.classList.add('m-in'); });
    };
    enter();
    new MutationObserver(enter).observe(list, { childList: true });
    document.addEventListener('change', (e) => {
      const row = e.target instanceof Element ? e.target.closest('[data-quote-items] .quote-item') : null;
      if (!row) return;
      row.classList.remove('m-flash'); void row.offsetWidth; row.classList.add('m-flash');
    });
  }

  /* ---------- 5. Product photo cross-fade on pack change ---------- */
  const fadeMain = () => {
    const main = document.querySelector('[data-main-image], #product-hero-image');
    if (!main || reduce) return;
    main.classList.remove('m-fade'); void main.offsetWidth; main.classList.add('m-fade');
  };
  document.addEventListener('change', (e) => { if (e.target instanceof Element && e.target.matches('[data-quote-add] select[name="id"]')) fadeMain(); });
  // Product cards: choosing a pack (e.g. the drum) shows that pack's photo on the card.
  document.addEventListener('change', (e) => {
    const sel = e.target;
    if (!(sel instanceof HTMLSelectElement) || !sel.matches('.product-card [data-quote-add] select[name="id"]')) return;
    const img = sel.closest('.product-card')?.querySelector('.product-image img');
    const opt = sel.selectedOptions[0];
    const src = opt?.dataset.previewImage || opt?.dataset.image;
    if (!img || !src) return;
    img.removeAttribute('srcset');
    img.src = /[?&]width=\d+/.test(src) ? src.replace(/([?&]width=)\d+/, '$1650') : src;
    if (!reduce) { img.classList.remove('m-fade'); void img.offsetWidth; img.classList.add('m-fade'); }
  });
  document.addEventListener('click', (e) => { if (e.target instanceof Element && e.target.closest('[data-image-src]')) fadeMain(); });

  /* ---------- 6. Light scroll reveals for headings and product cards below the fold ---------- */
  if (!reduce && 'IntersectionObserver' in window) {
    const targets = [...document.querySelectorAll('main .vt-heading, main .section-heading, main .product-card')]
      .filter((el) => el.getBoundingClientRect().top > window.innerHeight);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px' });
    targets.forEach((el) => {
      const siblings = [...el.parentElement.children].filter((c) => c.classList.contains('product-card'));
      el.style.setProperty('--m-i', Math.max(0, siblings.indexOf(el)) % 4);
      el.classList.add('m-reveal');
      io.observe(el);
    });
  }
})();
